/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { ConfigurableNavigatorFactory } from './ConfigurableNavigatorFactory';
import { MixNavigatorFactory } from './MixNavigatorFactory';
import { PanNavigator } from './Navigators/PanNavigator';
import { PinchRollNavigator } from './Navigators/PinchRollNavigator';
import { RotateAroundNavigator } from './Navigators/RotateAroundNavigator';
import { StraightAheadNavigator } from './Navigators/StraightAheadNavigator';
import { LookAroundNavigator } from './Navigators/LookAroundNavigator';
var MouseButton;
(function (MouseButton) {
    MouseButton[MouseButton["LEFT"] = 0] = "LEFT";
    MouseButton[MouseButton["MIDDLE"] = 1] = "MIDDLE";
    MouseButton[MouseButton["RIGHT"] = 2] = "RIGHT";
})(MouseButton || (MouseButton = {}));
/**
 * Helpers to create default navigation scheme
 */
export class DefaultNavigators {
    static initOrbit(stage, navigatorName = 'default', { pan, rotateAround, pinchRoll, straightAhead } = {}) {
        const defaultConfig = {
            rotateAround: {
                speed: 1,
                allowedMouseBtns: [MouseButton.LEFT],
                allowPanDuringPinch: false,
            },
            straightAhead: {
                speed: 1,
                mousePanFactor: 1,
                wheelTouchFactor: 1,
                allowedMouseBtns: [MouseButton.MIDDLE],
            },
            pan: {
                speed: 1,
                allowedMouseBtns: [MouseButton.RIGHT],
                allowPanDuringPinch: true,
                allowPanOnlyAtPinch: true,
            },
            pinchRoll: {
                speed: 1,
                mousePanFactor: 1,
                allowedMouseBtns: [MouseButton.MIDDLE],
                allowPanDuringPinch: false,
            },
        };
        const el = stage.getRenderer().domElement;
        const panFactory = new ConfigurableNavigatorFactory((cam) => new PanNavigator(cam, el));
        panFactory.configure(Object.assign({}, defaultConfig.pan, pan));
        const rotateAroundFactory = new ConfigurableNavigatorFactory((cam) => new RotateAroundNavigator(cam, el));
        rotateAroundFactory.configure(Object.assign({}, defaultConfig.rotateAround, rotateAround));
        const pinchRollFactory = new ConfigurableNavigatorFactory((cam) => new PinchRollNavigator(cam, el));
        pinchRollFactory.configure(Object.assign({}, defaultConfig.pinchRoll, pinchRoll));
        const straightAheadFactory = new ConfigurableNavigatorFactory((cam) => new StraightAheadNavigator(cam, el));
        straightAheadFactory.configure(Object.assign({}, defaultConfig.straightAhead, straightAhead));
        const factory = new MixNavigatorFactory(panFactory, straightAheadFactory, rotateAroundFactory, pinchRollFactory);
        stage.addNavigatorFactory(navigatorName, factory);
        return {
            pan: panFactory,
            rotateAround: rotateAroundFactory,
            pinchRoll: pinchRollFactory,
            straightAhead: straightAheadFactory,
        };
    }
    static initFirstPerson(stage, navigatorName = 'default', { pan, lookAround, pinchRoll, straightAhead } = {}) {
        const defaultConfig = {
            lookAround: {
                speed: 1,
                allowedMouseBtns: [MouseButton.LEFT],
                allowPanDuringPinch: false,
            },
            straightAhead: {
                speed: 1,
                mousePanFactor: 1,
                wheelTouchFactor: 1,
                allowedMouseBtns: [MouseButton.MIDDLE],
            },
            pan: {
                speed: 1,
                allowedMouseBtns: [MouseButton.RIGHT],
                allowPanDuringPinch: true,
                allowPanOnlyAtPinch: true,
            },
            pinchRoll: {
                speed: 1,
                mousePanFactor: 1,
                allowedMouseBtns: [MouseButton.MIDDLE],
                allowPanDuringPinch: false,
            },
        };
        const el = stage.getRenderer().domElement;
        const panFactory = new ConfigurableNavigatorFactory((cam) => new PanNavigator(cam, el));
        panFactory.configure(Object.assign({}, defaultConfig.pan, pan));
        const lookAroundFactory = new ConfigurableNavigatorFactory((cam) => new LookAroundNavigator(cam, el));
        lookAroundFactory.configure(Object.assign({}, defaultConfig.lookAround, lookAround));
        const pinchRollFactory = new ConfigurableNavigatorFactory((cam) => new PinchRollNavigator(cam, el));
        pinchRollFactory.configure(Object.assign({}, defaultConfig.pinchRoll, pinchRoll));
        const straightAheadFactory = new ConfigurableNavigatorFactory((cam) => new StraightAheadNavigator(cam, el));
        straightAheadFactory.configure(Object.assign({}, defaultConfig.straightAhead, straightAhead));
        const factory = new MixNavigatorFactory(panFactory, straightAheadFactory, lookAroundFactory, pinchRollFactory);
        stage.addNavigatorFactory(navigatorName, factory);
        return {
            pan: panFactory,
            lookAround: lookAroundFactory,
            pinchRoll: pinchRollFactory,
            straightAhead: straightAheadFactory,
        };
    }
    static init2D(stage, navigatorName = 'default', { pan, straightAhead } = {}) {
        const defaultConfig = {
            pan: {
                speed: .025,
                allowedMouseBtns: [MouseButton.RIGHT],
                allowPanDuringPinch: true,
                allowPanOnlyAtPinch: true,
            },
            straightAhead: {
                speed: .1,
                mousePanFactor: 1,
                wheelTouchFactor: 1,
                allowedMouseBtns: [MouseButton.MIDDLE],
                allowPanDuringPinch: false,
            },
        };
        const el = stage.getRenderer().domElement;
        const panFactory = new ConfigurableNavigatorFactory((cam) => new PanNavigator(cam, el));
        panFactory.configure(Object.assign({}, defaultConfig.pan, pan));
        const straightAheadFactory = new ConfigurableNavigatorFactory((cam) => {
            const nav = new StraightAheadNavigator(cam, el);
            nav.setIsMovementAllowed((cm) => cm.position.z > 0.00001); // camera should not zoom beyond the image
            return nav;
        });
        straightAheadFactory.configure(Object.assign({}, defaultConfig.straightAhead, straightAhead));
        const factory = new MixNavigatorFactory(panFactory, straightAheadFactory);
        stage.addNavigatorFactory(navigatorName, factory);
        return {
            pan: panFactory,
            straightAhead: straightAheadFactory,
        };
    }
}
//# sourceMappingURL=DefaultNavigators.js.map